import { useQuery } from '@tanstack/react-query'
import { CalendarPlus, CalendarX2, Paperclip } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { downloadFile } from '@/api/files'
import { toApiError } from '@/api/errors'
import { EmptyState } from '@/components/feedback/States'
import { toast } from '@/components/feedback/toast-store'
import { DataTable, type Column } from '@/components/tables/DataTable'
import { Button } from '@/components/ui/Button'
import { Card, CardHeader } from '@/components/ui/Card'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { Dialog } from '@/components/ui/Dialog'
import { formatNumber } from '@/utils/format'
import {
  scopeBalancesQuery,
  scopeRequestsQuery,
  scopeTypesQuery,
  useCancelLeave,
  useSubmitLeave,
  type LeaveScope,
} from '../api'
import { formatDays, formatRange, type LeaveRequest } from '../types'
import { WORDING, type LeaveWording } from '../wording'
import { BalanceCards } from './BalanceCards'
import { LeaveStatusBadge, LeaveTypeBadge } from './LeaveBadges'
import { LeaveRequestForm } from './LeaveRequestForm'

function RequestLeaveDialog({
  scope,
  subject,
  wording,
  onOpenChange,
}: {
  scope: LeaveScope
  subject: string
  wording: LeaveWording
  onOpenChange: (open: boolean) => void
}) {
  const { t } = useTranslation('leave')
  const w = WORDING[wording]
  const types = useQuery(scopeTypesQuery(scope))
  const balances = useQuery(scopeBalancesQuery(scope))
  const submit = useSubmitLeave(scope)
  const [submitting, setSubmitting] = useState(false)

  return (
    <Dialog
      open
      onOpenChange={onOpenChange}
      dismissible={!submitting}
      icon={<CalendarPlus />}
      title={t(w.request)}
      description={t('For {{subject}}. The school office will review it.', { subject })}
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={submitting}>
            {t('Cancel')}
          </Button>
          <Button type="submit" form="leave-request-form" loading={submitting} disabled={!types.data}>
            {t('Send request')}
          </Button>
        </>
      }
    >
      {types.data ? (
        <LeaveRequestForm
          formId="leave-request-form"
          wording={wording}
          types={types.data}
          balances={balances.data}
          onSubmittingChange={setSubmitting}
          onSubmit={async (values) => {
            const created = await submit.mutateAsync(values)
            toast.success(
              t(w.requested),
              `${created.type.name} : ${formatRange(created)} (${formatDays(created.days)}).`,
            )
            onOpenChange(false)
          }}
        />
      ) : (
        <p className="py-6 text-center text-sm text-muted">
          {types.error ? toApiError(types.error).message : t(w.loadingTypes)}
        </p>
      )}
    </Dialog>
  )
}

/**
 * Self-service leave: balances, a request form and the request history with cancellation.
 * Used by the staff portal ("My leave") and the family portal (per child, worded as absences).
 */
export function LeaveSelfService({
  scope,
  subject,
  wording = 'leave',
}: {
  scope: LeaveScope
  subject: string
  wording?: LeaveWording
}) {
  const { t } = useTranslation('leave')
  const w = WORDING[wording]
  const balances = useQuery(scopeBalancesQuery(scope))
  const requests = useQuery(scopeRequestsQuery(scope))
  const cancel = useCancelLeave(scope)
  const [requesting, setRequesting] = useState(false)
  const [cancelling, setCancelling] = useState<LeaveRequest | null>(null)

  const columns: Column<LeaveRequest>[] = [
    {
      key: 'type',
      header: t('Type'),
      cell: (r) => <LeaveTypeBadge name={r.type.name} colour={r.type.colour} />,
    },
    {
      key: 'dates',
      header: t('Dates'),
      cell: (r) => <span className="whitespace-nowrap">{formatRange(r)}</span>,
    },
    {
      key: 'days',
      header: t('Days'),
      align: 'right',
      cell: (r) => <span className="tabular">{formatNumber(r.days)}</span>,
    },
    {
      key: 'reason',
      header: t('Reason'),
      hideBelow: 'lg',
      cell: (r) => (
        <div className="max-w-xs">
          <p className="truncate">{r.reason}</p>
          {r.decisionNote ? (
            <p className="truncate text-xs text-muted">{t('Office: {{note}}', { note: r.decisionNote })}</p>
          ) : null}
        </div>
      ),
    },
    { key: 'status', header: t('Status'), cell: (r) => <LeaveStatusBadge status={r.status} /> },
    {
      key: 'actions',
      header: <span className="sr-only">{t('Actions')}</span>,
      align: 'right',
      cell: (r) => (
        <div className="flex justify-end gap-1">
          {r.attachment ? (
            <Button
              variant="ghost"
              size="sm"
              leftIcon={<Paperclip />}
              onClick={() =>
                void downloadFile(r.attachment?.downloadUrl ?? '', r.attachment?.name ?? 'attachment')
              }
              aria-label={t('Download {{name}}', { name: r.attachment.name })}
            >
              {t('File')}
            </Button>
          ) : null}
          {r.canCancel ? (
            <Button variant="ghost" size="sm" onClick={() => setCancelling(r)}>
              {t('Cancel')}
            </Button>
          ) : null}
        </div>
      ),
    },
  ]

  return (
    <div className="space-y-6">
      <section aria-labelledby="leave-balances-title" className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 id="leave-balances-title" className="text-base font-semibold text-ink">
            {t(w.balances)}
          </h2>
          <Button leftIcon={<CalendarPlus />} onClick={() => setRequesting(true)}>
            {t(w.request)}
          </Button>
        </div>
        <BalanceCards
          wording={wording}
          balances={balances.data}
          loading={balances.isPending}
          error={balances.error}
          onRetry={() => void balances.refetch()}
        />
      </section>

      <Card>
        <CardHeader title={t('Requests')} description={t(w.cancellable)} />
        <DataTable
          caption={t(w.caption)}
          columns={columns}
          rows={requests.data}
          getRowId={(r) => r.id}
          loading={requests.isPending}
          error={requests.error}
          onRetry={() => void requests.refetch()}
          skeletonRows={4}
          mobileCard={(r) => (
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <LeaveTypeBadge name={r.type.name} colour={r.type.colour} />
                <LeaveStatusBadge status={r.status} />
              </div>
              <p className="text-sm font-medium text-ink">
                {formatRange(r)} · {formatDays(r.days)}
              </p>
              <p className="line-clamp-2 text-xs text-muted">{r.reason}</p>
              {r.canCancel ? (
                <Button variant="secondary" size="sm" onClick={() => setCancelling(r)}>
                  {t('Cancel request')}
                </Button>
              ) : null}
            </div>
          )}
          empty={
            <EmptyState
              compact
              icon={<CalendarX2 />}
              title={t(w.empty)}
              description={t('Requests you send appear here with their status.')}
            />
          }
        />
      </Card>

      {requesting ? (
        <RequestLeaveDialog scope={scope} subject={subject} wording={wording} onOpenChange={setRequesting} />
      ) : null}
      <ConfirmDialog
        open={cancelling !== null}
        onOpenChange={(o) => (o ? undefined : setCancelling(null))}
        title={t(w.confirmCancel)}
        description={cancelling ? `${cancelling.type.name}, ${formatRange(cancelling)}.` : undefined}
        confirmLabel={t('Cancel request')}
        cancelLabel={t('Keep it')}
        loading={cancel.isPending}
        onConfirm={() =>
          cancelling &&
          cancel.mutate(cancelling.id, {
            onSuccess: () => {
              toast.success(t('Request cancelled'))
              setCancelling(null)
            },
            onError: (e) => toast.error(t('Could not cancel'), toApiError(e).message),
          })
        }
      />
    </div>
  )
}
